/**
 * services/web-ui/src/lib/stage-descriptions.ts
 *
 * Plain-English descriptions and role tags for each call stage.
 * Used by stage tooltips in TrackView and FlowchartView.
 */

export type StageRole =
  | "open"
  | "compliance"
  | "discovery"
  | "data"
  | "action"
  | "close"
  | "unknown";

export interface StageInfo {
  description: string;
  role: StageRole;
}

export const STAGE_DESCRIPTIONS: Record<string, StageInfo> = {
  CONVERSING: {
    description: "Conversation active — actions remain subject to permissions and confirmation",
    role: "discovery",
  },
  GREETING: {
    description: "Initial connect — audio established, call begins",
    role: "open",
  },
  INTRODUCTION: {
    description: "Vooice introduces itself by name",
    role: "open",
  },
  DISCLOSE_AI: {
    description: "Vooice declares it is an AI (required disclosure)",
    role: "compliance",
  },
  CONSENT_REQUEST: {
    description: "Asking the caller for recording consent",
    role: "compliance",
  },
  CONSENT_RESPONSE: {
    description: "Caller replies yes or no to recording consent",
    role: "compliance",
  },
  GATHER_PURPOSE: {
    description: "Establishing why we're on this call",
    role: "discovery",
  },
  ESTABLISH_GOAL: {
    description: "A concrete, specific goal has been stated",
    role: "discovery",
  },
  FETCH_INFO: {
    description: "Reading from calendar, contacts, or memory",
    role: "data",
  },
  NEGOTIATE: {
    description: "Back-and-forth with caller converging on options",
    role: "discovery",
  },
  READBACK: {
    description: "Reciting key details aloud before committing",
    role: "compliance",
  },
  CONFIRM: {
    description: "Caller confirms (or corrects) the readback",
    role: "compliance",
  },
  COMMIT: {
    description: "Writing the change — calendar event, email, etc.",
    role: "action",
  },
  FAREWELL: {
    description: "Wrap-up and goodbye",
    role: "close",
  },
  END: {
    description: "Call disconnected",
    role: "close",
  },
  // Voicemail workflow stages
  VOICEMAIL_DETECT: {
    description: "Deciding whether we reached a human or voicemail",
    role: "data",
  },
  LEAVE_VOICEMAIL: {
    description: "Speaking the voicemail message",
    role: "action",
  },
  // Voice-engine runtime macro-states
  WAITING_ON_HOLD: {
    description: "On hold — waiting for a human to return to the line",
    role: "data",
  },
  NAVIGATING_IVR: {
    description: "Working through an automated phone menu",
    role: "data",
  },
  PASSTHROUGH: {
    description: "Owner is on the line — agent is listening only",
    role: "action",
  },
  WRAP_UP: {
    description: "Closing out the call — recap, follow-ups, reminders",
    role: "close",
  },
};

/** Role → Tailwind color classes for blocks and badges */
export const ROLE_COLORS: Record<StageRole, { bg: string; border: string; text: string; hex: string }> = {
  open:       { bg: "bg-blue-900/50",    border: "border-blue-600",   text: "text-blue-300",   hex: "#3b82f6" },
  compliance: { bg: "bg-orange-900/50",  border: "border-orange-500", text: "text-orange-300", hex: "#f97316" },
  discovery:  { bg: "bg-purple-900/50",  border: "border-purple-600", text: "text-purple-300", hex: "#a855f7" },
  data:       { bg: "bg-teal-900/50",    border: "border-teal-600",   text: "text-teal-300",   hex: "#14b8a6" },
  action:     { bg: "bg-green-900/50",   border: "border-green-600",  text: "text-green-300",  hex: "#22c55e" },
  close:      { bg: "bg-gray-800/60",    border: "border-gray-600",   text: "text-gray-400",   hex: "#6b7280" },
  unknown:    { bg: "bg-gray-800/40",    border: "border-gray-700",   text: "text-gray-500",   hex: "#374151" },
};

/** Look up stage info, returning a sensible fallback for unknown stages */
export function getStageInfo(stage: string): StageInfo {
  return STAGE_DESCRIPTIONS[stage] ?? { description: stage, role: "unknown" };
}

/** Look up role color set for a given stage */
export function getStageColors(stage: string): { bg: string; border: string; text: string; hex: string } {
  const info = getStageInfo(stage);
  return ROLE_COLORS[info.role];
}
